import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { Alert, AlertDescription } from '../ui/alert'
import { useAuth } from '../../lib/auth.context'
import { ROUTES } from 'src/config/routes'
import { InputOTP } from '../input-otp'

interface TOTPVerifyProps {
  onVerifySuccess?: () => void
  isEmbedded?: boolean
}

export function TOTPVerify({ onVerifySuccess, isEmbedded = false }: TOTPVerifyProps) {
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const { status, verifyMFA } = useAuth()

  useEffect(() => {
    // When the auth status changes to authenticated, we know MFA was successful
    // and the context has been updated. Now it's safe to navigate.
    console.log('[TOTPVerify] Status:', status)
    if (status === 'authenticated') {
      // If a success handler is provided, don't navigate, just call it.
      if (onVerifySuccess) return

      const redirectTo = location.state?.from?.pathname || ROUTES.PARTNER.DASHBOARD
      navigate(redirectTo, { replace: true })
    }
  }, [status, navigate, location, onVerifySuccess])

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      await verifyMFA(verifyCode)
      // The useEffect above will handle the redirect on successful state change.
      toast({
        title: 'Success',
        description: 'Two-factor authentication successful.',
      })
      // If a success handler is provided, call it now that we know verification was successful
      if (onVerifySuccess) {
        onVerifySuccess()
      }
    } catch (error: any) {
      const errorMessage =
        error.code === 'invalid_grant' ? 'The code you entered is invalid. Please try again.' : error.message || 'Failed to verify code.'
      setFormError(errorMessage)
      toast({
        title: 'Verification Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const renderEmbedded = () => (
    <form onSubmit={handleVerify} className="flex flex-col items-center w-full gap-6">
      <div className="flex flex-col items-center gap-8 w-full">
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <InputOTP length={6} onChange={(value) => setVerifyCode(value)} />
        <Button
          type="submit"
          disabled={isLoading || !verifyCode}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </Button>
      </div>
    </form>
  )

  const renderFullPage = () => (
    <div className="w-full min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}/>
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8"/>
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 w-full flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md">
                <h1 className="text-4xl font-bold mb-4 text-white">
                  2FA Verification
                </h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Login Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-medium text-white">Enter the 6-digit code from your authentication app</h1>
                <p className="text-slate-400 font-light">
                  (Google Authenticator, Microsoft Authenticator, etc).
                </p>
              </div>
              <div>
                <form onSubmit={handleVerify}>
                  <div className="flex flex-col items-center gap-8 w-full">
                    {formError && (
                      <Alert variant="destructive">
                        <AlertDescription>{formError}</AlertDescription>
                      </Alert>
                    )}
                    <InputOTP length={6} onChange={(value) => setVerifyCode(value)} />
                    <Button
                      type="submit"
                      disabled={isLoading || !verifyCode}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      {isLoading ? 'Verifying...' : 'Verify Code'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return isEmbedded ? renderEmbedded() : renderFullPage()
}
